/* global customElements, CSS */
// <size-guide-popup>: custom size guide (Find my size + Size chart). Spec: Obsidian plans/2026-10-05-size-guide-design.md
(function () {
  'use strict';

  function cmToIn(cm, step) {
    step = step || 0.5;
    return Math.round(cm / 2.54 / step) * step;
  }

  function usableRows(rows) {
    return (rows || [])
      .filter(function (r) { return r && Number.isFinite(r.min) && Number.isFinite(r.max); })
      .slice()
      .sort(function (a, b) { return a.min - b.min; });
  }

  // Exact range wins. In a gap or above every range: the largest size whose max is below
  // the height (between two sizes, take the smaller). Below every range: the first size.
  function pickSize(rows, cm) {
    var sorted = usableRows(rows);
    if (!sorted.length || !Number.isFinite(cm)) return null;
    var hit = sorted.find(function (r) { return cm >= r.min && cm <= r.max; });
    if (hit) return hit;
    var below = null;
    sorted.forEach(function (r) { if (r.max < cm) below = r; });
    return below || sorted[0];
  }

  function heightBounds(rows) {
    var sorted = usableRows(rows);
    if (!sorted.length) return null;
    return { min: sorted[0].min, max: Math.max.apply(null, sorted.map(function (r) { return r.max; })) };
  }

  function formatRange(row, unit) {
    if (unit === 'in') return cmToIn(row.min) + '–' + cmToIn(row.max) + ' in';
    return row.min + '–' + row.max + ' cm';
  }

  function formatMeasure(value, unit) {
    if (value == null || value === '') return '—';
    return unit === 'in' ? cmToIn(value) + ' in' : value + ' cm';
  }

  // Chart columns in display order. kind: text = shown as stored; len = cm/in; weight = kg/lb.
  var COLUMNS = [
    { key: 'eu', kind: 'text' }, { key: 'age', kind: 'text' },
    { key: 'height', kind: 'len', lo: 'min', hi: 'max', step: 0.5 },
    { key: 'chest', kind: 'len', lo: 'chest', hi: 'chest_max', step: 0.25 },
    { key: 'waist', kind: 'len', lo: 'waist', hi: 'waist_max', step: 0.25 },
    { key: 'hips', kind: 'len', lo: 'hips', hi: 'hips_max', step: 0.25 },
    { key: 'weight', kind: 'weight', lo: 'weight', hi: 'weight_max' },
    { key: 'foot', kind: 'len', lo: 'foot', hi: 'foot_max', step: 0.25 },
    { key: 'head', kind: 'len', lo: 'head', hi: 'head_max', step: 0.25 },
    { key: 'hand', kind: 'len', lo: 'hand', hi: 'hand_max', step: 0.25 },
    { key: 'neck', kind: 'len', lo: 'neck', hi: 'neck_max', step: 0.25 },
    { key: 'length', kind: 'len', lo: 'length', hi: 'length_max', step: 0.5 },
    { key: 'uk', kind: 'text' }, { key: 'us_shoe', kind: 'text' }, { key: 'jp', kind: 'text' }
  ];

  function kgToLb(kg) {
    return Math.round(kg * 2.20462 * 2) / 2;
  }

  function colFirst(row, col) {
    return col.kind === 'text' ? row[col.key] : row[col.lo];
  }

  function chartColumns(rows) {
    return COLUMNS.filter(function (c) {
      return (rows || []).some(function (r) { var v = colFirst(r, c); return v != null && v !== ''; });
    }).map(function (c) { return c.key; });
  }

  // "Also try": the sizes either side of the picked one, in the product's option order.
  function alsoTry(rows, value) {
    var i = (rows || []).findIndex(function (r) { return (r.value || r.size) === value; });
    if (i < 0) return [];
    return rows.slice(Math.max(0, i - 1), i + 2);
  }

  // The variant with this size and the current variant's other options (colour…).
  function variantForSize(variants, currentId, sizeIndex, size) {
    var list = variants || [];
    var cur = list.find(function (v) { return String(v.id) === String(currentId); });
    var match = list.filter(function (v) { return v.options[sizeIndex] === size; });
    if (cur) {
      var same = match.find(function (v) {
        return v.options.every(function (o, i) { return i === sizeIndex || o === cur.options[i]; });
      });
      if (same) return same;
    }
    return match[0] || null;
  }

  // Result card: every column this row has a value for, minus the measure the finder slid on.
  function resultFields(row, measureKey) {
    return chartColumns([row]).filter(function (k) { return k !== measureKey; });
  }

  function cellText(row, key, unit) {
    var col = COLUMNS.find(function (c) { return c.key === key; });
    if (!col) return '—';
    if (col.kind === 'text') {
      var t = row[col.key];
      return t == null || t === '' ? '—' : String(t);
    }
    var lo = row[col.lo], hi = row[col.hi];
    if (lo == null) return '—';
    if (hi == null) hi = lo;
    var conv, suffix;
    if (col.kind === 'weight') {
      conv = unit === 'in' ? kgToLb : function (x) { return x; };
      suffix = unit === 'in' ? ' lb' : ' kg';
    } else {
      conv = unit === 'in' ? function (x) { return cmToIn(x, col.step); } : function (x) { return x; };
      suffix = unit === 'in' ? ' in' : ' cm';
    }
    var a = conv(lo), b = conv(hi);
    return (a === b ? String(a) : a + '–' + b) + suffix;
  }

  // Chest/waist hold one "up to" value per size: the smallest size whose value fits the body.
  // Above every value → the last size that has the field, flagged `over`.
  function pickByMax(rows, field, body) {
    var sorted = usableRows(rows).filter(function (r) { return Number.isFinite(r[field]); });
    if (!sorted.length || !Number.isFinite(body)) return null;
    var hit = sorted.find(function (r) { return r[field] >= body; });
    return hit ? { row: hit, over: false } : { row: sorted[sorted.length - 1], over: true };
  }

  // Largest size any entered measurement needs. Ties keep the earlier measure (height, chest, waist).
  function pickSizeMulti(rows, m) {
    var sorted = usableRows(rows);
    m = m || {};
    if (!sorted.length) return null;
    var candidates = [];
    if (Number.isFinite(m.height)) {
      var h = pickSize(sorted, m.height);
      if (h) candidates.push({ row: h, decidedBy: 'height', over: false });
    }
    ['chest', 'waist'].forEach(function (field) {
      if (!Number.isFinite(m[field])) return;
      var r = pickByMax(sorted, field, m[field]);
      if (r) candidates.push({ row: r.row, decidedBy: field, over: r.over });
    });
    if (!candidates.length) return null;
    return candidates.reduce(function (best, c) {
      return sorted.indexOf(c.row) > sorted.indexOf(best.row) ? c : best;
    });
  }

  function measureBounds(rows, field) {
    var values = (rows || []).map(function (r) { return r && r[field]; }).filter(Number.isFinite);
    if (!values.length) return null;
    return { min: Math.floor(Math.min.apply(null, values) - 4), max: Math.ceil(Math.max.apply(null, values) + 4) };
  }

  function clamp(v, min, max) {
    if (!Number.isFinite(v)) return null;
    return Math.min(max, Math.max(min, v));
  }

  function midpoint(b, step) {
    return Math.round(((b.min + b.max) / 2) / step) * step;
  }

  // Starting value for an untouched chest/waist slider: the value of the size height picks,
  // so it can never out-vote height before the shopper moves it.
  function extraDefault(rows, field, heightCm) {
    var row = pickSize(rows, heightCm);
    return row && Number.isFinite(row[field]) ? row[field] : null;
  }

  // Shopify's t filter HTML-escapes, and the JSON payload carries that output; decode once.
  function decodeEntities(str) {
    var named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
    return String(str == null ? '' : str).replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, function (m, e) {
      if (e[0] === '#') return String.fromCharCode(e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10));
      return named[e.toLowerCase()];
    });
  }

  // The measure Find my size slides on, per guide. Clothing keeps "between two sizes, take the
  // smaller" (don't size up); footwear and accessories take the bigger one (feet grow mid-season).
  var FINDER_MEASURES = [
    { key: 'height', lo: 'min', hi: 'max', between: 'smaller', step: 1 },
    { key: 'foot', lo: 'foot', hi: 'foot_max', between: 'bigger', step: 0.5 },
    { key: 'head', lo: 'head', hi: 'head_max', between: 'bigger', step: 0.5 },
    { key: 'hand', lo: 'hand', hi: 'hand_max', between: 'bigger', step: 0.5 },
    { key: 'neck', lo: 'neck', hi: 'neck_max', between: 'bigger', step: 0.5 },
    { key: 'length', lo: 'length', hi: 'length_max', between: 'bigger', step: 1 }
  ];

  function finderMeasure(rows) {
    return FINDER_MEASURES.find(function (m) {
      return (rows || []).some(function (r) { return r && Number.isFinite(r[m.lo]); });
    }) || null;
  }

  function measureRows(rows, m) {
    return (rows || [])
      .filter(function (r) { return r && Number.isFinite(r[m.lo]); })
      .map(function (r) { return { row: r, lo: r[m.lo], hi: Number.isFinite(r[m.hi]) ? r[m.hi] : r[m.lo] }; })
      .sort(function (a, b) { return a.lo - b.lo; });
  }

  function finderBounds(rows, m) {
    var mr = measureRows(rows, m);
    if (!mr.length) return null;
    var lo = mr[0].lo;
    var hi = Math.max.apply(null, mr.map(function (x) { return x.hi; }));
    // Single-value charts (shoe insole length): leave room below the smallest size.
    if (mr.every(function (x) { return x.lo === x.hi; })) lo = lo - 1;
    return { min: Math.floor(lo), max: Math.ceil(hi) };
  }

  function pickByMeasure(rows, m, v) {
    var mr = measureRows(rows, m);
    if (!mr.length || !Number.isFinite(v)) return null;
    var hit = mr.find(function (x) { return v >= x.lo && v <= x.hi; });
    if (hit) return { row: hit.row, over: false };
    if (m.between === 'bigger') {
      var up = mr.find(function (x) { return x.lo > v; });
      return up ? { row: up.row, over: false } : { row: mr[mr.length - 1].row, over: true };
    }
    var below = null;
    mr.forEach(function (x) { if (x.hi < v) below = x; });
    return { row: (below || mr[0]).row, over: false };
  }

  function parseSaved(raw) {
    var out = { height: null, chest: null, waist: null, foot: null, head: null, hand: null, neck: null, length: null, extrasOpen: false, unit: null };
    var o;
    try { o = JSON.parse(raw); } catch (e) { return out; }
    if (!o || typeof o !== 'object') return out;
    ['height', 'chest', 'waist', 'foot', 'head', 'hand', 'neck', 'length'].forEach(function (k) {
      if (typeof o[k] === 'number' && Number.isFinite(o[k])) out[k] = o[k];
    });
    out.extrasOpen = o.extrasOpen === true;
    if (o.unit === 'cm' || o.unit === 'in') out.unit = o.unit;
    return out;
  }

  if (typeof window === 'undefined' && typeof module !== 'undefined') {
    module.exports = {
      cmToIn: cmToIn, pickSize: pickSize, heightBounds: heightBounds, formatRange: formatRange, formatMeasure: formatMeasure,
      pickByMax: pickByMax, pickSizeMulti: pickSizeMulti, measureBounds: measureBounds, clamp: clamp, midpoint: midpoint, parseSaved: parseSaved,
      extraDefault: extraDefault, decodeEntities: decodeEntities,
      kgToLb: kgToLb, chartColumns: chartColumns, cellText: cellText, resultFields: resultFields, alsoTry: alsoTry, variantForSize: variantForSize,
      finderMeasure: finderMeasure, finderBounds: finderBounds, pickByMeasure: pickByMeasure
    };
    return;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var STORE_KEY = 'dpd:sizeGuide:v1';
  function readStore() {
    try { return parseSaved(window.sessionStorage.getItem(STORE_KEY)); } catch (e) { return parseSaved(null); }
  }
  function writeStore(state) {
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(state)); } catch (e) {}
  }
  function clearStore() {
    try { window.sessionStorage.removeItem(STORE_KEY); } catch (e) {}
  }

  function defineElement() {
    if (customElements.get('size-guide-popup')) return true;
    if (!window.CustomDialog) return false;

    class SizeGuidePopup extends window.CustomDialog {
      connectedCallback() {
        super.connectedCallback();
        // A property, not a data attribute: an outerHTML copy of this element must still initialise.
        if (this._sgInitialized) return; // reconnects must not double-bind
        this._sgInitialized = true;
        // A colour swap inserts this element via innerHTML; re-query in case the base
        // constructor ran before the children existed.
        // The base class binds the backdrop click only when this.dialog existed in its
        // constructor, so bind it here once (✕ is covered by the base's document click handler).
        if (!this.dialog) this.dialog = this.querySelector('.popup__content');
        if (this.dialog && !this.dialog.dataset.sgBound) {
          this.dialog.dataset.sgBound = '1';
          this.dialog.addEventListener('click', (e) => { if (e.target === e.currentTarget) this.closeModal(); });
        }
        var data = { unit: 'cm', rows: [], strings: {} };
        try { data = JSON.parse(this.querySelector('script[data-size-guide-json]').textContent); } catch (e) {}
        this.rows = data.rows || [];
        this.strings = data.strings || {};
        Object.keys(this.strings).forEach((k) => { this.strings[k] = decodeEntities(this.strings[k]); });
        this.marketUnit = data.unit === 'in' ? 'in' : 'cm';
        var saved = readStore();
        this.userUnit = saved.unit;
        this.unit = saved.unit || this.marketUnit;

        if (!this.rows.length) { this.showComingSoon(); return; }

        // Animated close on Escape, like the trust modal.
        this.dialog && this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.closeModal(); });
        this.querySelectorAll('[data-sg-tab]').forEach((b) => b.addEventListener('click', () => this.showTab(b.dataset.sgTab)));
        this.querySelectorAll('[data-sg-unit]').forEach((b) => b.addEventListener('click', () => this.setUnit(b.dataset.sgUnit, true)));
        this.variants = data.variants || [];
        this.sizeIndex = Number(data.size_index) || 0;
        this.querySelector('[data-sg-result]').addEventListener('click', (e) => {
          var t = e.target.closest('[data-sg-try]');
          if (t) { this.tryValue = t.dataset.sgTry; this.renderFinder(); return; }
          var btn = e.target.closest('[data-sg-choose]');
          if (btn) this.chooseSize(btn.dataset.sgChoose);
        });
        var pills = this.querySelector('[data-sg-pills]');
        if (pills) pills.addEventListener('click', (e) => {
          var p = e.target.closest('[data-sg-pill]');
          if (p) { this.chartValue = p.dataset.sgPill; this.renderChart(); }
        });
        this.querySelectorAll('[data-sg-scroll]').forEach((b) => b.addEventListener('click', () => {
          if (pills) pills.scrollBy({ left: Number(b.dataset.sgScroll) * pills.clientWidth * 0.8, behavior: 'smooth' });
        }));

        this.slider = this.querySelector('[data-sg-slider]');
        // What Find my size slides on: height for clothing, foot/head/hand for accessories.
        this.measure = finderMeasure(this.rows);
        this.bounds = this.measure ? finderBounds(this.rows, this.measure) : null;
        if (this.bounds) {
          var mk = this.measure.key;
          this.slider.step = this.measure.step;
          this.slider.min = this.bounds.min;
          this.slider.max = this.bounds.max;
          var h = clamp(saved[mk], this.bounds.min, this.bounds.max);
          this.slider.value = h != null ? h : midpoint(this.bounds, this.measure.step);
          this.slider.addEventListener('input', () => { this.renderFinder(); this.save(); });
          var hintText = this.strings['slide_hint_' + mk];
          if (hintText) {
            var hint = this.querySelector('.dpd-sg__hint');
            if (hint) hint.textContent = hintText;
            this.slider.setAttribute('aria-label', hintText);
          }
        }

        this.extras = {};
        ['chest', 'waist'].forEach((field) => {
          var box = this.querySelector('[data-sg-extra="' + field + '"]');
          if (!box) return;
          var b = measureBounds(this.rows, field);
          if (!b) { box.hidden = true; return; }
          var slider = box.querySelector('[data-sg-extra-slider]');
          slider.min = b.min;
          slider.max = b.max;
          var v = clamp(saved[field], b.min, b.max);
          slider.value = v != null ? v : midpoint(b, 0.5);
          // Untouched sliders don't count; renderFinder keeps them on the height-picked size.
          var x = { box: box, slider: slider, bounds: b, touched: saved[field] != null };
          slider.addEventListener('input', () => { x.touched = true; this.renderFinder(); this.save(); });
          this.extras[field] = x;
        });
        this.extrasOpen = saved.extrasOpen && Object.keys(this.extras).length > 0;
        var toggle = this.querySelector('[data-sg-extras-toggle]');
        if (toggle) toggle.addEventListener('click', () => { this.extrasOpen = !this.extrasOpen; this.applyExtrasOpen(); this.renderFinder(); this.save(); });
        var resetBtn = this.querySelector('[data-sg-reset]');
        if (resetBtn) resetBtn.addEventListener('click', () => this.reset());
        this.applyExtrasOpen();
        this.hasFinder = !!this.bounds;
        if (toggle && !Object.keys(this.extras).length) toggle.hidden = true;
        if (!this.hasFinder) {
          var scope = this.closest('product-info') || document;
          this.querySelectorAll('[data-sg-tab="finder"]').forEach((b) => { b.hidden = true; });
          // The theme sets display:flex on these buttons, so the hidden attribute alone loses; use Dawn's .hidden.
          scope.querySelectorAll('[data-sg-open="finder"]').forEach((b) => { b.hidden = true; b.classList.add('hidden'); });
        }
        this.setUnit(this.unit);
        this.showTab(this.hasFinder ? 'finder' : 'chart');
      }

      // Guide assigned but none of its rows match this product's sizes.
      showComingSoon() {
        var scope = this.closest('product-info') || document;
        var buttons = scope.querySelector('.size-assistant__wrapper[data-size-guide="custom"] .size-assistant__buttons');
        if (buttons) {
          var p = document.createElement('p');
          p.className = 'dpd-sg__coming-soon';
          p.textContent = this.strings.coming_soon || '';
          buttons.replaceWith(p);
        }
      }

      open(tab) {
        if (!this.rows || !this.rows.length || !this.dialog) return;
        this.chartValue = null; // the chart opens on the size selected on the page
        this.renderChart();
        this.showTab(this.hasFinder && tab !== 'chart' ? 'finder' : 'chart');
        this.openModal();
      }

      showTab(tab) {
        requestAnimationFrame(() => this.centerPill());
        var title = this.querySelector('[data-sg-media-title]');
        if (title) title.textContent = tab === 'chart' ? (this.strings.tab_chart || '') : (this.strings.tab_finder || '');
        this.querySelectorAll('[data-sg-panel]').forEach((p) => p.classList.toggle('active', p.dataset.sgPanel === tab));
        this.querySelectorAll('[data-sg-tab]').forEach((b) => {
          var on = b.dataset.sgTab === tab;
          b.classList.toggle('active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }

      setUnit(unit, fromUser) {
        this.unit = unit === 'in' ? 'in' : 'cm';
        if (fromUser) { this.userUnit = this.unit; this.save(); }
        this.querySelectorAll('[data-sg-unit]').forEach((b) => b.setAttribute('aria-pressed', b.dataset.sgUnit === this.unit ? 'true' : 'false'));
        this.renderFinder();
        this.renderChart();
      }

      applyExtrasOpen() {
        var box = this.querySelector('[data-sg-extras]');
        var toggle = this.querySelector('[data-sg-extras-toggle]');
        if (box) box.hidden = !this.extrasOpen;
        if (toggle) {
          toggle.setAttribute('aria-expanded', this.extrasOpen ? 'true' : 'false');
          toggle.textContent = this.extrasOpen ? toggle.dataset.labelOpen : toggle.dataset.labelClosed;
        }
      }

      measurements() {
        var m = { height: this.bounds && this.measure.key === 'height' ? Number(this.slider.value) : null, chest: null, waist: null };
        if (this.extrasOpen) {
          Object.keys(this.extras).forEach((f) => { if (this.extras[f].touched) m[f] = Number(this.extras[f].slider.value); });
        }
        return m;
      }

      // Fields this product's guide doesn't have keep their saved values, so a height-only
      // guide never wipes the chest/waist the shopper entered on another product.
      save() {
        var prev = readStore();
        var hasExtras = Object.keys(this.extras || {}).length > 0;
        // Every measure is kept separately: a boot page saves the foot and keeps the saved height.
        var state = Object.assign({}, prev, {
          chest: this.extras.chest ? (this.extras.chest.touched ? Number(this.extras.chest.slider.value) : null) : prev.chest,
          waist: this.extras.waist ? (this.extras.waist.touched ? Number(this.extras.waist.slider.value) : null) : prev.waist,
          extrasOpen: hasExtras ? !!this.extrasOpen : prev.extrasOpen,
          unit: this.userUnit || null
        });
        if (this.bounds) state[this.measure.key] = Number(this.slider.value);
        writeStore(state);
      }

      // Back to the start state for this product and forget everything saved.
      reset() {
        clearStore();
        this.userUnit = null;
        if (this.bounds) this.slider.value = midpoint(this.bounds, this.measure.step);
        Object.keys(this.extras).forEach((f) => { this.extras[f].touched = false; });
        this.extrasOpen = false;
        this.applyExtrasOpen();
        this.setUnit(this.marketUnit);
      }

      // The Size control inside this product's <variant-selects>: radio pills or a dropdown.
      sizeControl() {
        var name = this.dataset.optionName;
        var scope = this.closest('product-info') || document;
        var picker = scope.querySelector('variant-selects');
        if (!name || !picker) return null;
        var select = picker.querySelector('select[name="' + CSS.escape('options[' + name + ']') + '"]');
        if (select) return { select: select };
        return { radios: picker.querySelectorAll('input[type="radio"][name="' + CSS.escape(name) + '"]') };
      }

      selectedSize() {
        var c = this.sizeControl();
        if (!c) return null;
        if (c.select) return c.select.value;
        var checked = Array.prototype.find.call(c.radios, function (r) { return r.checked; });
        return checked ? checked.value : null;
      }

      renderFinder() {
        var out = this.querySelector('[data-sg-result]');
        var read = this.querySelector('[data-sg-read]');
        if (!this.rows.length || !this.slider.max) { out.innerHTML = ''; read.textContent = ''; return; }
        var cm = Number(this.slider.value);
        var isHeight = this.measure.key === 'height';
        var label = this.unit === 'in' ? cmToIn(cm, isHeight ? 0.5 : 0.25) + ' in' : cm + ' cm';
        read.textContent = label;
        this.slider.setAttribute('aria-valuetext', label);
        Object.keys(this.extras || {}).forEach((f) => {
          var x = this.extras[f];
          if (!x.touched) {
            var d = clamp(extraDefault(this.rows, f, cm), x.bounds.min, x.bounds.max);
            x.slider.value = d != null ? d : midpoint(x.bounds, 0.5);
          }
          var txt = x.touched ? formatMeasure(Number(x.slider.value), this.unit) : '—';
          x.box.querySelector('[data-sg-extra-read]').textContent = txt;
          x.slider.setAttribute('aria-valuetext', txt);
        });
        var pick;
        if (isHeight) {
          pick = pickSizeMulti(this.rows, this.measurements());
        } else {
          var one = pickByMeasure(this.rows, this.measure, cm);
          pick = one && { row: one.row, decidedBy: this.measure.key, over: one.over };
        }
        if (!pick) { out.innerHTML = ''; return; }
        var best = pick.row;
        var bestValue = best.value || best.size;
        if (this.pickValue !== bestValue) { this.pickValue = bestValue; this.tryValue = null; }
        var tryValue = this.tryValue || bestValue;
        var row = this.rowByValue(tryValue) || best;
        var s = this.strings;
        var unit = this.unit;
        var fields = resultFields(row, this.measure.key);
        var meas = fields.length ? '<div class="dpd-sg__meas">' + fields.map(function (k) {
          return '<span><span class="dpd-sg__meas-lab">' + esc(s['col_' + k] || k) + '</span>' + esc(cellText(row, k, unit)) + '</span>';
        }).join('') + '</div>' : '';
        var tries = alsoTry(this.rows, bestValue);
        var pills = tries.length > 1 ? '<p class="dpd-sg__also">' + esc(s.also_try) + '</p><div class="dpd-sg__pills dpd-sg__pills--try" role="group">'
          + tries.map(function (r) {
            var v = r.value || r.size;
            return '<button type="button" class="dpd-sg__pill' + (v === bestValue ? ' is-best' : '') + '" data-sg-try="' + esc(v) + '" aria-pressed="' + (v === tryValue ? 'true' : 'false') + '">' + esc(r.size) + '</button>';
          }).join('') + '</div>' : '';
        out.innerHTML = '<div class="dpd-sg__best">'
          + '<span class="dpd-sg__best-size">' + esc(best.size) + '</span>'
          + '<div><p class="dpd-sg__best-txt">' + esc(s.best_option) + '</p>'
          + '<p class="dpd-sg__range">' + esc(this.measure.key === 'height' ? formatRange(best, unit) : cellText(best, this.measure.key, unit)) + '</p></div></div>'
          + (pick.decidedBy === 'chest' || pick.decidedBy === 'waist' ? '<p class="dpd-sg__decided" data-sg-decided>' + esc(s['decided_by_' + pick.decidedBy]) + '</p>' : '')
          + (pick.over ? '<p class="dpd-sg__over" data-sg-over>' + esc(s.over_chart) + '</p>' : '')
          + pills
          + meas
          + this.chooseButton(row);
      }

      rowByValue(value) {
        return this.rows.find(function (r) { return (r.value || r.size) === value; }) || null;
      }

      // "Add size X to cart", or a disabled sold-out note when that size can't be bought.
      chooseButton(row) {
        var s = this.strings;
        var value = row.value || row.size;
        var v = this.variantFor(value);
        if (v && !v.available) {
          return '<button type="button" class="dpd-sg__choose button button--primary" disabled>' + esc(String(s.sold_out || '').replace('[size]', row.size)) + '</button>';
        }
        return '<button type="button" class="dpd-sg__choose button button--primary" data-sg-choose="' + esc(value) + '">'
          + esc(String(s.add_to_cart || s.choose_size || '').replace('[size]', row.size)) + '</button>';
      }

      variantFor(value) {
        var scope = this.closest('product-info') || document;
        var idInput = scope.querySelector('.staticAddToCart input[name="id"]') || scope.querySelector('form[action*="/cart/add"] input[name="id"]');
        return variantForSize(this.variants, idInput && idInput.value, this.sizeIndex, value);
      }

      // SizeBay-style chart: size pills + the picked size's measurements as a list.
      renderChart() {
        var pillsBox = this.querySelector('[data-sg-pills]');
        var list = this.querySelector('[data-sg-list]');
        if (!pillsBox || !list) return;
        var s = this.strings, unit = this.unit;
        var current = this.chartValue || this.selectedSize() || this.pickValue;
        var row = this.rowByValue(current) || this.rows[0];
        var value = row.value || row.size;
        var self = this;
        pillsBox.innerHTML = this.rows.map(function (r) {
          var v = r.value || r.size;
          var sold = self.variantFor(v);
          return '<button type="button" class="dpd-sg__pill' + (sold && !sold.available ? ' is-sold-out' : '') + '" data-sg-pill="' + esc(v) + '" aria-pressed="' + (v === value ? 'true' : 'false') + '">' + esc(r.size) + '</button>';
        }).join('');
        list.innerHTML = resultFields(row, null).map(function (k) {
          return '<div class="dpd-sg__list-row"><dt>' + esc(s['col_' + k] || k) + '</dt><dd>' + esc(cellText(row, k, unit)) + '</dd></div>';
        }).join('');
        this.centerPill();
      }

      // Bring the picked size into view; a no-op while the chart panel is hidden (zero width).
      centerPill() {
        var box = this.querySelector('[data-sg-pills]');
        var on = box && box.querySelector('[aria-pressed="true"]');
        if (on && box.clientWidth) box.scrollLeft = on.offsetLeft - box.offsetLeft - (box.clientWidth - on.offsetWidth) / 2;
      }

      // Selects the size, then adds that variant to the cart through the theme's own form
      // (cart drawer, GWP and analytics all run as for a normal Add to cart click).
      chooseSize(size) {
        var scope = this.closest('product-info') || document;
        var target = this.variantFor(size);
        var c = this.sizeControl();
        this.closeModal();
        if (!c) return;
        var idNow = (scope.querySelector('.staticAddToCart input[name="id"]') || {}).value;
        if (c.select) {
          c.select.value = size;
          c.select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          var input = Array.prototype.find.call(c.radios, function (r) { return r.value === size; });
          if (!input) return;
          if (!input.checked) input.click();
        }
        if (!target || !target.available) {
          var atc0 = scope.querySelector('.staticAddToCart') || scope.querySelector('[name="add"]');
          if (atc0) setTimeout(function () { atc0.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 220);
          return;
        }
        // The variant change re-renders the buy buttons; wait until the form carries the new variant.
        var started = Date.now();
        var tick = function () {
          var root = scope.isConnected ? scope : document;
          var id = root.querySelector('.staticAddToCart input[name="id"]');
          var btn = root.querySelector('.staticAddToCart .product-form__submit');
          var ready = id && String(id.value) === String(target.id) && btn && !btn.disabled
            && btn.getAttribute('aria-disabled') !== 'true' && !btn.classList.contains('loading');
          if (ready && (String(idNow) === String(target.id) || Date.now() - started > 150)) { btn.click(); return; }
          if (Date.now() - started < 5000) setTimeout(tick, 100);
        };
        setTimeout(tick, 50);
      }
    }

    customElements.define('size-guide-popup', SizeGuidePopup);
    return true;
  }

  // custom-dialog.js may load after this file; retry once the DOM is parsed.
  if (!defineElement()) document.addEventListener('DOMContentLoaded', defineElement);

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-sg-open]');
    if (!trigger) return;
    var scope = trigger.closest('product-info') || document;
    var popup = scope.querySelector('size-guide-popup');
    if (popup && typeof popup.open === 'function') popup.open(trigger.dataset.sgOpen);
  });
})();
